// Settings.jsx
/* global $3Dmol */

import React, { useEffect } from 'react';
import './SettingsBlock.css';
import { ShowMotif, ShowSurface} from '../utils/index.js';
import { PLDDT_BANDS } from '../utils/plddtColor.js';
import { chainColor } from '../utils/chainColor.js';

const Settings = ({
  viewerRef,
  currentColor,
  opacity,
  setOpacity,
  surfaceRef,
  isSurfaceShown,
  setIsSurfaceShown,
  currentStyle,
  setCurrentStyle,
  activeMotifs,
  setActiveMotifs,
  motifs,
  ligands,
  activeLigands,
  setActiveLigands,
  hasStructure,
  showMotifSideChains,
  setShowMotifSideChains,
  navigateToRecord,
  isPredicted,
  colorByPlddt,
  setColorByPlddt,
  chains = []
}) => {


  // Surface hook
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !isSurfaceShown) return;

    viewer.removeAllSurfaces();
    
    const surface = viewer.addSurface($3Dmol.SurfaceType.VDW, {
      opacity,
      color: "SkyBlue",
      colorscheme: "Chain"
    });

    surfaceRef.current = surface;
    viewer.render();
    
  }, [opacity, isSurfaceShown, viewerRef, surfaceRef]);


  // Style Func
  const applyStyle = (styleType) => {
    
    if (styleType !== 'cartoon') {
      setActiveMotifs([]);
    }
    
    
    setCurrentStyle(styleType);
    
  };

  // Surface button handler
  const handleToggleSurface = async () => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    try {
      
      if (isSurfaceShown) {
        viewer.removeAllSurfaces();
        surfaceRef.current = null;
        setIsSurfaceShown(false);
      } else {
        
        await new Promise(resolve => {
          ShowSurface(viewerRef, currentColor, opacity, surfaceRef);
          setTimeout(resolve, 100);
        });
        setIsSurfaceShown(true);
      }
    } catch (error) {
      console.error("Error toggling surface:", error);
    }
  };

  
  const availableMotifs = (motifs || []).filter(m => m.ranges?.length > 0); // filter out empty motifs


  //
  /*
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !hasStructure) return;

    const baseStyles = {
      cartoon: { cartoon: { color: currentColor } },
      spheres: { sphere: { colorscheme: "ssPyMol", radius: 1.3 } },
      lines: { line: { colorscheme: "ssPyMol", linewidth: 2 } },
      sticks: { stick: { colorscheme: "greenCarbon", radius: 0.3 } }
    };

    const style = baseStyles[currentStyle] || baseStyles.cartoon;
    viewer.setStyle({}, style);

    if (currentStyle === 'cartoon' && activeMotifs.length > 0) {
      activeMotifs.forEach(motifName => {
        const motif = availableMotifs.find(m => m.name === motifName);
        if (motif && motif.ranges) {
          motif.ranges.forEach(([start, end]) => {
            const selection = { resi: [...Array(end - start + 1).keys()].map(i => start + i) };
            viewer.setStyle(selection, { cartoon: { color: motif.color } });
          });
        }
      });
    }

    viewer.render();
  }, [activeMotifs, currentStyle, currentColor, viewerRef, availableMotifs, hasStructure]);
  */



  const handleMotifClick = (motifName) => {
    setActiveMotifs(prevMotifs => {
      // if motif is already active, remove it
      if (prevMotifs.includes(motifName)) {
        return prevMotifs.filter(m => m !== motifName);
      } 
      // Otherwise, add it to the array (new click)
      else {
        return [...prevMotifs, motifName];
      }
    });
  };

  const handleLigandClick = (ligand) => {
    if (ligand.persistent_id) {
      navigateToRecord(ligand.persistent_id, 'molecule');
    } else {
      alert('This molecule does not have a record page.');
    }
  };

  return (
    <div className="settings-container">
      {/* Group for style */}
      <fieldset className="settings-group">
        <legend>Style</legend>
    
        <div className="settings-group-content">
          <div className="buttons-row">
            <button  type="button" onClick={() => applyStyle('cartoon')} className={`settings-button ${currentStyle === 'cartoon' ? 'active' : ''}`}>
              Cartoon
            </button>
            <button  type="button" onClick={() => applyStyle('spheres')} className={`settings-button ${currentStyle === 'spheres' ? 'active' : ''}`}>
              Spheres
            </button>
            <button  type="button" onClick={() => applyStyle('lines')} className={`settings-button ${currentStyle === 'lines' ? 'active' : ''}`}>
              Lines
            </button>
            <button  type="button" onClick={() => applyStyle('sticks')} className={`settings-button ${currentStyle === 'sticks' ? 'active' : ''}`}>
              Sticks
            </button>
          </div>
        </div>
      </fieldset>

      {/* Group for pLDDT coloring (predicted structures only) */}
      {isPredicted && (
        <fieldset className="settings-group">
          <legend>Confidence (pLDDT)</legend>
          <div className="settings-group-content">
            <button
              type="button"
              onClick={() => setColorByPlddt(prev => !prev)}
              className={`settings-button ${colorByPlddt ? 'active' : ''}`}
            >
              {colorByPlddt ? 'Disable pLDDT coloring' : 'Enable pLDDT coloring'}
            </button>
            {colorByPlddt && (
              <ul className="plddt-legend">
                {PLDDT_BANDS.map(band => (
                  <li key={band.color} className="plddt-legend-item">
                    <span
                      className="plddt-legend-swatch"
                      style={{ backgroundColor: band.color }}
                    />
                    <span className="plddt-legend-label">{band.label}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </fieldset>
      )}

      {/* Chain coloring legend (shown whenever the structure is not pLDDT-colored,
          i.e. experimental structures and predicted ones with pLDDT off) */}
      {!colorByPlddt && chains.length > 0 && (
        <fieldset className="settings-group">
          <legend>Coloring (by chain)</legend>
          <div className="settings-group-content">
            <ul className="plddt-legend">
              {chains.map(chain => (
                <li key={chain} className="plddt-legend-item">
                  <span
                    className="plddt-legend-swatch"
                    style={{ backgroundColor: chainColor(chain) }}
                  />
                  <span className="plddt-legend-label">Chain {chain}</span>
                </li>
              ))}
            </ul>
          </div>
        </fieldset>
      )}

      {/* Group for surface */}
      <fieldset className="settings-group">
        <legend>Surface</legend>

        <div className="settings-group-content">
          <button type="button" onClick={handleToggleSurface} className={`settings-button ${isSurfaceShown ? 'active' : ''}`}>
            {isSurfaceShown ? 'Hide Surface' : 'Show Surface'}
          </button>
          <label htmlFor="opacitySlider">Opacity: {opacity.toFixed(2)}</label>
          <input
            type="range"
            id="opacitySlider"
            className="opacity-slider"
            min="0"
            max="1"
            step="0.05"
            value={opacity}
            onChange={(e) => setOpacity(parseFloat(e.target.value))}
            disabled={!isSurfaceShown}
          />
        </div>
      </fieldset>


      { /* Group for motifs */}
      {availableMotifs.length > 0 && (
        <fieldset className="settings-group">
          <legend>Structure Motifs</legend>
          <div className="settings-group-content">
            <div className="buttons-row">
              {availableMotifs.map((motif) => (
                <button type="button"
                  key={motif.name}
                  onClick={() => handleMotifClick(motif.name)}
                  disabled={currentStyle !== 'cartoon'}
                  className={`settings-button ${activeMotifs.includes(motif.name) ? 'active' : ''}`}>
                  {motif.name}
                  <span className="motif-color" style={{ backgroundColor: motif.color }}></span>
                </button>
              ))}
            </div>
            {currentStyle !== 'cartoon' && (
              <p className="motif-info-text">
                (Available only in Cartoon style)
              </p>
            )}
          </div>
          
          {activeMotifs.length > 0 && (
            <button
              type="button"
              onClick={() => setShowMotifSideChains(prev => !prev)}
              disabled = {currentStyle !== 'cartoon'}
              className= {`settings-button ${showMotifSideChains ? 'active' : ''}`}
              style = {{ marginTop: '10px', width: '100%' }}>
              {showMotifSideChains ? 'Hide Side Chains' : 'Show Side Chains'}
            </button>
          )}
          {currentStyle !== 'cartoon' && (
            <p className="motif-info-text">
              (Side chains can be shown only in Cartoon style)
            </p>

          )}


        </fieldset>
      )}

      
      {ligands && ligands.length > 0 && (
        <div className="settings-group">
          <div className="settings-title">Ligands</div>
          <div className="settings-options-col">
            {ligands.map(ligand => (
              <div key={ligand.molecule_id} className="ligand-row">
                <label className="settings-checkbox">
                  <input
                    type="checkbox"
                    checked={activeLigands.includes(ligand.molecule_id)}
                    onChange={() => {
                      setActiveLigands(prev =>
                        prev.includes(ligand.molecule_id)
                          ? prev.filter(id => id !== ligand.molecule_id)
                          : [...prev, ligand.molecule_id]
                      );
                    }}
                  />
                </label>
                <button 
                  type="button"
                  className="ligand-name-button"
                  onClick={() => handleLigandClick(ligand)}
                  title={ligand.persistent_id ? `Go to record ${ligand.persistent_id}` : 'No record page available'}
                >
                  {ligand.name || `Ligand ${ligand.molecule_id}`}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
